import { useTranslation } from 'react-i18next';
import { FormItem, Select } from 'Components';
import { useFormikContext } from 'formik';
import { AddUserValues, EditUserValues } from 'Pages/User/User.types';
import { SelectProps } from 'Components/Form/Select/Select';
import { roleToOption } from 'Pages/User/EditUser/EditUser.helpers';
import { sortBy } from 'lodash';
import { useUserContext } from '../UserContext';
import { translateTInfo } from 'i18n/i18n.helpers';
import { useCustomTenantRolesDropdownList, useUserTenantRoles } from '../EditUser/tabs/ModuleSettings/ModuleSettingsContainer';
import { useEffect, useMemo } from 'react';
import { SystemRoleType } from 'Services/Api/Roles/Types';
import { Checkbox } from 'antd';
import {
	customTenantRoleBelongsToSelectedGroups,
	toCustomTenantRoleOptionKey
} from '../helpers/tenantRoleAssignment';
import { PermissionCode } from 'Services/Api/Users/Types';
import { useStores } from 'Hooks/useStore';
import { useDashboardContext } from 'Pages/Dashboard/DashboardContext';

const FIELD_NAME = 'primarySystemRoleId';

interface RoleFieldProps {
	isProfile?: boolean;
	peakV3Id?: string;
}
function RoleField(props: RoleFieldProps) {
	const { t } = useTranslation();
	const formik = useFormikContext<AddUserValues>();
	const {
		values,
		initialValues,
		setFieldValue,
		getFieldMeta
	} = formik;
	const meta = getFieldMeta(FIELD_NAME);
	const { systemRoles, systemRolesList } = useUserContext();
	const { setToastInfo } = useDashboardContext();
	const { authStore } = useStores();
	const canAssignSecondaryRole = authStore.currentUser?.primarySystemRole?.permissions?.some(r =>
		r.code === PermissionCode.ASSIGN_SECONDARY_ROLE ||
		r.code === PermissionCode.CREATE_SECONDARY_ROLE ||
		r.code === PermissionCode.EDIT_SECONDARY_ROLE ||
		r.code === PermissionCode.DELETE_SECONDARY_ROLE
	);
	useUserTenantRoles();

	const mappedGroups = useMemo(
		() =>
			values.groups?.map((gp) => ({
				key: gp.key,
				value: gp.key,
				label: gp.label,
				data: gp.data
			})) ?? [],
		[values.groups]
	);

	const { customTenantRolesDropdownList, loading: customRolesLoading } = useCustomTenantRolesDropdownList(canAssignSecondaryRole ? mappedGroups : []);

	const customRoleOptions = useMemo(
		() =>
			customTenantRolesDropdownList.map((role) => {
				const key = toCustomTenantRoleOptionKey(role.id);
				return {
					key,
					value: key,
					label: role.name,
					tenantId: role.rootGroupId,
					selectedGroupId: mappedGroups[0]?.key,
					rootGroupId: role.rootGroupId,
					data: {
						id: role.id,
						role: role.name,
						isPrimary: false,
						isCustomTenantRole: true,
						tenantId: role.rootGroupId,
						moduleSettings: role.moduleSettings
					}
				};
			}),
		[customTenantRolesDropdownList, mappedGroups]
	);

	const systemRoleOptions = useMemo(
		() =>
			systemRolesList?.map((role) => {
				const option = roleToOption(role);
				return {
					...option,
					data: {
						...option.data,
						isCustomTenantRole: false
					}
				};
			}) ?? [],
		[systemRolesList]
	);

	const roleOptions = useMemo(
		() => [...systemRoleOptions, ...customRoleOptions],
		[systemRoleOptions, customRoleOptions]
	);

	const roleKeysSignature = (roles: AddUserValues['primarySystemRoleId'] = []) =>
		roles
			.map((role) => String(role?.key ?? role?.value ?? ''))
			.filter(Boolean)
			.sort()
			.join(',');

	const onChange: SelectProps['onChange'] = (selectedValue, option) => {
		let selected = (Array.isArray(selectedValue)
			? selectedValue
			: selectedValue
				? [selectedValue]
				: Array.isArray(option)
					? option
					: option
						? [option]
						: []) as AddUserValues['primarySystemRoleId'];
		if (!canAssignSecondaryRole) {
			setToastInfo?.({
				type: 'warning',
				message: t('errorCodes.NO_SECONDARY_PERMISSIONS')
			});
			const selectedKeys = new Set(selected.map((o: any) => String(o?.key ?? o?.value)));
			const blockedRemoved = (values.primarySystemRoleId ?? []).filter(
				(role: any) => role.data?.isCustomTenantRole && !selectedKeys.has(String(role.key))
			);
			selected = [...selected, ...blockedRemoved];
		}
		const mappedOptions = selected.map((opt: any) => {
			const optionKey = String(opt?.key ?? opt?.value ?? '');
			const fromInitial = initialValues.primarySystemRoleId?.find(
				(item) => String(item.key) === optionKey
			);
			if (fromInitial) return fromInitial;
			const fromList = roleOptions.find(
				(item) => String(item.key) === optionKey
			) as any;
			const current = values.primarySystemRoleId?.find(
				(item) => String(item.key) === optionKey
			) as any;

			return {
				key: opt.key ?? fromList?.key ?? current?.key,
				label: opt.label ?? fromList?.label ?? current?.label,
				value: opt.value ?? fromList?.value ?? current?.value,
				data: opt.data ?? fromList?.data ?? current?.data,
				rootGroupId:
					opt.rootGroupId ??
					opt.tenantId ??
					fromList?.tenantId ??
					fromList?.rootGroupId ??
					current?.rootGroupId,
				selectedGroupId:
					opt.selectedGroupId ??
					fromList?.selectedGroupId ??
					current?.selectedGroupId ??
					mappedGroups[0]?.key
			};
		});

		const currentPrimaryRole = values.primarySystemRoleId?.find(
			(role: any) => role.data?.isPrimary
		);

		const selectedPrimaryRole = mappedOptions.find(
			(role: any) =>
				role.data?.isPrimary &&
				String(role.key) !== String(currentPrimaryRole?.key)
		);

		let nextRoles: AddUserValues['primarySystemRoleId'];

		if (selectedPrimaryRole) {
			const isSuperAdmin =
				selectedPrimaryRole.data?.role?.toLowerCase() ===
				SystemRoleType.SuperAdmin;

			if (isSuperAdmin) {
				setFieldValue(FIELD_NAME, [selectedPrimaryRole]);
				setFieldValue('groups', []);
				setFieldValue('moduleSettings', undefined);
				return;
			}

			nextRoles = [
				selectedPrimaryRole,
				...mappedOptions.filter((role: any) => !role.data?.isPrimary)
			];
		} else {
			nextRoles = mappedOptions;
		}

		nextRoles = sortBy(nextRoles, (role) => !role.data?.isPrimary);

		if (
			roleKeysSignature(nextRoles) ===
			roleKeysSignature(values.primarySystemRoleId)
		) {
			return;
		}

		setFieldValue(FIELD_NAME, nextRoles);
		const hasPrimary = nextRoles.some(role => role.data?.isPrimary);
		if (!hasPrimary) {
			formik.setFieldTouched(FIELD_NAME, true, true);
		}
	};

	useEffect(() => {
		const groups = values.groups ?? [];
		const currentRoles = values.primarySystemRoleId ?? [];

		const updatedRoles = currentRoles.filter((role: any) => {
			if (!role.data?.isCustomTenantRole) {
				return true;
			}

			return customTenantRoleBelongsToSelectedGroups(role, groups);
		});

		if (updatedRoles.length !== currentRoles.length) {
			setFieldValue(FIELD_NAME, updatedRoles);
		}
	}, [values.groups, values.primarySystemRoleId, setFieldValue]);

	const currentValue = values.primarySystemRoleId;
	const currentValueRole = currentValue?.find((item) => item.data?.isPrimary);
	const isSuperAdmin = currentValueRole?.data?.role?.toLowerCase() === SystemRoleType.SuperAdmin;
	const isEditingUser = Boolean(
		(values as EditUserValues).peakV3Id || props.peakV3Id
	);
	const originalIsSuperAdmin =
		initialValues.primarySystemRoleId
			?.find((item) => item.data?.isPrimary)
			?.data?.role?.toLowerCase() === SystemRoleType.SuperAdmin;
	const error = meta.touched ? meta.error : undefined;
	const isLoading = systemRoles.loading || customRolesLoading;
	return (
		<FormItem
			label={t('user.primarySystemRoleId')}
			required
			validateStatus={(error && 'error') || undefined}
			help={translateTInfo(error, t)}
		>
			<Select
				mode="multiple"
				className={`custom-multi-select ${(currentValue?.length || isLoading) ? "" : "show-icon"}`}
				placeholder={t('profile.general.placeholders.role')}
				loading={isLoading}
				labelInValue
				optionLabelProp="label"
				onChange={onChange}
				disabled={
					props.isProfile ||
					isLoading ||
					(isEditingUser && originalIsSuperAdmin)
				}
				value={currentValue}
				optionFilterProp="label"
				showSearch
			>
				{roleOptions?.map((option) => {
					const isSuperAdminOption =
						option.data?.role?.toLowerCase() ===
						SystemRoleType.SuperAdmin;
					const disabled =
						(option.data?.isCustomTenantRole && !canAssignSecondaryRole) ||
						(isSuperAdmin && !option.data?.isPrimary) ||
						(isEditingUser &&
							!originalIsSuperAdmin &&
							isSuperAdminOption);
					const isActive = (currentValue || []).some(
						(item) => String(item.key) === String(option.key)
					);
					return (
						<Select.Option {...option} disabled={disabled}>
							<Checkbox checked={isActive} disabled={disabled} style={{ pointerEvents: 'none' }}>{option.label}</Checkbox>
						</Select.Option>
					);
				})}
			</Select>
		</FormItem>
	);
}

export default RoleField;
